import {observable, autorun, computed, map} from 'mobx';

import * as IsEmail from 'isemail';
let isEmail = IsEmail.default;

class User {
  @observable id=null;
  @observable id_customer = null;
  @observable customer_name = null;
  @observable id_home_location = null;
  @observable name = null;
  @observable email = null;
  @observable phone = null;
  @observable phone_verified = null;
  @observable verifycode = null;
  @observable password = null;
  @observable repeat_password = null;
  @observable roles=[];
  @observable receiveJobCompleteReports=false;
  @observable receiveInformationEmail=false;
  @observable receiveSMSNotifications=false;
  @observable deleted=false;
  @observable language='EN';
  @observable serviceOrgMember = false;
  @observable user_id_locked = false;

  // When registering new users, we should run a background check
  // to verify that the selected user login is unique
  @observable login_unique=false;
  @observable login_checked=false;
  @observable email_unique=false;
  @observable email_checked=false;

  @computed get passwordsDiffer() {
    // If both are empty strings or null:
    if(!this.password && !this.repeat_password) return false;
    return this.password != this.repeat_password;
  }

  @computed get validEmail() {
    if(!this.email) return false;
    return isEmail(this.email,{minDomainAtoms:2});
  }

  @computed get debugLine() {
    return [ this.id,
             this.name,
             this.email ].join(", ");
  }

  // User needs to pick a different username :
  @computed get notUnique() {
    return this.login_checked && !this.login_unique;
  }

  // This function must return true for the
  // allowing updates towards DB
  @computed get isValidEdit() {
    // If we want to delete the user and we have the ID, OK :
    if (this.deleted && this.id ) return true;
    if (!this.name ) return false;
    if (!this.id ) return false;
    // We do not allow the backslash in a user name
    if (this.id.indexOf("\\")!=-1) return false;
    if (!this.id_customer ) return false;
    if (!this.email ) return false;
    if (this.roles.length<1 ) return false;
    if (!this.validEmail ) return false;
    return true;
  }
  // This function must return true for the
  // allowing updates towards DB
  @computed get isValidNewUser() {
    if (!this.login_checked ) return false;
    if (!this.login_unique ) return false;
    if (!this.name ) return false;
    if (!this.id ) return false;
    // We do not allow the backslash in a user name
    if (this.id.indexOf("\\")!=-1) return false;
    if (!this.id_customer ) return false;
    if (!this.email ) return false;
    if (this.email_checked!=this.email) return false;
    if (!this.email_unique) return false;
    if (this.roles.length<1 ) return false;
//    if (!(this.login_checked && this.login_unique)) return false;
    return true;
  }

  // This functions verifies fields used to register a new adminUser
  // user when creating a new customer
  @computed get isValidNewAdminUser() {
    if (!this.name ) return false;
    if (!this.id ) return false;
    // We do not allow the backslash in a user name
    if (this.id.indexOf("\\")!=-1) return false;
    if (!this.email ) return false;
    if (!this.validEmail ) return false;
    if (this.email_checked!=this.email) return false;
    if (!this.email_unique) return false;
    return true;
  }

  hasRole(id_role) {
    return this.roles.some((r)=>{
      return r.id_role==id_role;
    })
  }
  // Add a role to this user (from edit/new user forms)
  setRole(id_role,name) {
    if(this.hasRole(id_role)) return;
    this.roles.push({id_role,name})
  }
  // Remove a role from this user
  unsetRole(id_role) {
    this.roles = this.roles.filter((r)=>{
      return r.id_role!=id_role;
    })
  }
  // Toggle a role (check-box ui use)
  toggleRole(id_role,name) {
    if(this.hasRole(id_role)) {
      this.unsetRole(id_role);
    }
    else this.setRole(id_role,name);
  }

  // Used to merge data e.g. from server responses back into an existing
  // user object:
  copyFieldsFrom(otherUser) {
    var target = this;
    // We do not copy the password field, as it is only used as a one-way
    // set function.
    ['id',
    'id_customer',
    'serviceOrgMember',
    'customer_name',
    'name',
    'email',
    'phone',
    'phone_verified',
    'id_home_location',
    'deleted',
    'language',
    'receiveJobCompleteReports',
    'receiveInformationEmail',
    'receiveSMSNotifications',
    'user_id_locked'
    ].forEach( fieldname => {
      target[fieldname] = otherUser[fieldname];
    });
    // Make a copy of the role array:
    target.roles=otherUser.roles.map((r)=> { return r });
  }

}

// This method has to be in sync with the DataStore method that fetch the
// DB data.
function createUserFromDBRow(row) {
  var u = new User();
  u.id=row.id;
  u.id_customer=row.id_customer;
  u.serviceOrgMember=row.serviceOrgMember;
  u.customer_name=row.customer_name;
  u.id_home_location=row.home_location;
  if(u.id_home_location==null) {
    u.id_home_location="root";
  }
  u.name = row.name;
  u.email = row.email;
  u.phone = row.phone;
  u.phone_verified = row.phone_verified;
  u.roles = row.roles || [];
  u.deleted = row.deleted;
  u.language=row.language;
  u.receiveJobCompleteReports=row.receive_job_complete_reports;
  u.receiveInformationEmail = row.receive_information_email;
  u.receiveSMSNotifications=row.receive_sms_notifications;
  u.user_id_locked = row.user_id_locked;
  // However, if the phone number is missing, we drop the notification setting:
  // If e.g. phone number has been deleted in DB somehow:
  if(!u.phone) {
    u.receiveSMSNotifications=false;
  }
  return u;
}

// Make utility method available from Class (function)
User.createFromDBRow=createUserFromDBRow;

export default User;



/** WEBPACK FOOTER **
 ** ./src/DataModel/User.js
 **/